import { useEffect, useMemo, useState } from 'react';
import type { DescuentoDeLinea, LineaMostrador } from '@tanteo/compartido';
import { cubreProducto, desglosarVenta, formatearCOP } from '@tanteo/compartido';
import type { DescuentoDelNegocio } from '../../lib/api-descuentos';
import { DIGITOS_DE_PLATA } from '../../lib/lector';
import { Boton, CampoNumero, FilaAcciones, GrupoOpciones } from '../../ui/base';
import { Modal } from '../../ui/Modal';
import { textoDeDescuento } from './textoDeDescuento';

type ModoLibre = DescuentoDeLinea['tipo'];

/** El descuento de un producto con las columnas de la tabla del mostrador: precio de catálogo, descuento y precio
 *  de venta por unidad, y el subtotal. Ofrece los descuentos que el dueño configuró. */
export function DescuentoProducto({
  sku,
  lineas,
  descuentos,
  mostrarImpuesto,
  onAplicar,
  onCerrar,
}: {
  /** El producto al que se le descuenta; null cierra el diálogo. */
  sku: string | null;
  lineas: LineaMostrador[];
  /** Los descuentos a un producto que quien vende puede dar. */
  descuentos: DescuentoDelNegocio[];
  /** Con impuestos en el negocio, el diálogo lleva la misma columna de Impuesto que la tabla. */
  mostrarImpuesto: boolean;
  onAplicar: (sku: string, descuento: DescuentoDeLinea | null) => void;
  onCerrar: () => void;
}) {
  const linea = sku ? (lineas.find((l) => l.sku === sku) ?? null) : null;
  const previo = linea?.descuento ?? null;
  // Solo los que cubren este producto: un descuento con alcance no sale en los demás.
  const ofrecidos = linea ? descuentos.filter((d) => cubreProducto(d, linea.productoId, linea.categoria)) : [];
  const [elegido, setElegido] = useState<string | null>(null);
  const [modoLibre, setModoLibre] = useState<ModoLibre>('pct');
  const [valorLibre, setValorLibre] = useState<number | null>(null);

  // Cada apertura arranca con lo que la línea ya tenía, para corregirlo en vez de escribirlo de nuevo.
  useEffect(() => {
    if (!sku) return;
    setElegido(previo?.descuentoId ?? null);
    setModoLibre(previo?.libre ? previo.tipo : 'pct');
    setValorLibre(previo?.libre ? previo.valor : null);
  }, [sku, previo]);

  const escogido = ofrecidos.find((d) => d.id === elegido) ?? null;
  const candidato = useMemo((): DescuentoDeLinea | null => {
    if (!escogido) return null;
    const base = { descuentoId: escogido.id, nombre: escogido.nombre, libre: escogido.forma === 'libre' };
    if (escogido.forma === 'libre') {
      return valorLibre !== null && valorLibre > 0 ? { ...base, tipo: modoLibre, valor: valorLibre } : null;
    }
    if (escogido.forma === 'lleva_paga') {
      return { ...base, tipo: 'lleva_paga', valor: 0, lleva: escogido.lleva, paga: escogido.paga, maxVeces: escogido.maxVeces };
    }
    return {
      ...base,
      tipo: escogido.forma === 'porcentaje' ? 'pct' : 'monto_unidad',
      valor: escogido.valor ?? 0,
      maxUnidades: escogido.maxUnidades,
    };
  }, [escogido, modoLibre, valorLibre]);

  if (!linea || !sku) return null;
  // Con toda la venta: un lleva y paga junta las líneas que llevan el mismo descuento.
  const indice = lineas.findIndex((l) => l.sku === sku);
  const desglose = desglosarVenta(lineas.map((l) => (l.sku === sku ? { ...l, descuento: candidato } : l))).lineas[indice];
  const bruto = desglose?.bruto ?? 0;
  const neto = desglose?.neto ?? 0;
  const impuesto = desglose?.impuesto ?? 0;
  const unitarioNeto = Math.round(neto / linea.cantidad);
  const conDescuento = neto < bruto;
  const listo = candidato !== null && (conDescuento || candidato.tipo === 'lleva_paga');

  return (
    <Modal abierto titulo="Descuento producto" onCerrar={onCerrar}>
      <div className="space-y-4" data-e2e="descuento-producto">
        <GrupoOpciones
          etiqueta="Descuento"
          valor={elegido}
          onElegir={setElegido}
          opciones={ofrecidos.map((d) => ({
            valor: d.id,
            texto: textoDeDescuento(d, 'producto'),
            e2e: `descuento-opcion-${d.nombre}`,
          }))}
        />
        {escogido?.forma === 'libre' && (
          <div className="space-y-3">
            <GrupoOpciones
              etiqueta="Tipo de descuento libre"
              valor={modoLibre}
              onElegir={(m) => {
                setModoLibre(m);
                setValorLibre(null);
              }}
              opciones={[
                { valor: 'pct', texto: 'Porcentaje' },
                { valor: 'monto_unidad', texto: 'Rebaja $ por unidad' },
                { valor: 'precio_unidad', texto: 'Precio especial' },
              ]}
              e2e={(o) => `descuento-modo-${o.valor}`}
            />
            <CampoNumero
              etiqueta={
                modoLibre === 'pct' ? 'Porcentaje' : modoLibre === 'monto_unidad' ? 'Pesos menos por unidad' : 'Precio por unidad'
              }
              plata={modoLibre !== 'pct'}
              min={0}
              max={modoLibre === 'pct' ? 100 : undefined}
              valor={valorLibre}
              onValor={setValorLibre}
              lector={{ maxDigitos: DIGITOS_DE_PLATA }}
              data-e2e="descuento-valor"
            />
          </div>
        )}

        <div
          className={`bg-surface rounded-card divide-border num grid divide-x py-4 ${
            mostrarImpuesto ? 'grid-cols-[auto_1.5fr_repeat(5,1fr)]' : 'grid-cols-[auto_1.5fr_repeat(4,1fr)]'
          }`}
          data-e2e="descuento-producto-tabla"
        >
          <div className="px-4">
            <span className="text-muted block text-etiqueta font-bold uppercase">Cant.</span>
            <span className="text-titulo font-extrabold" data-e2e="descuento-producto-unidades">
              {linea.cantidad}
            </span>
          </div>
          <div className="min-w-0 px-4">
            <span className="text-muted block text-etiqueta font-bold uppercase">Producto</span>
            <span className="block truncate text-titulo font-extrabold" data-e2e="descuento-producto-nombre">
              {linea.nombre}
            </span>
          </div>
          <div className="px-4">
            <span className="text-muted block text-etiqueta font-bold uppercase">P. catálogo</span>
            <span className="text-titulo font-extrabold">{formatearCOP(linea.precioUnit)}</span>
          </div>
          <div className="px-4">
            <span className="text-muted block text-etiqueta font-bold uppercase">Descuento</span>
            <span className="text-destructive text-titulo font-extrabold" data-e2e="descuento-producto-rebaja">
              −{formatearCOP(linea.precioUnit - unitarioNeto)}
            </span>
          </div>
          <div className="px-4">
            <span className="text-muted block text-etiqueta font-bold uppercase">P. venta</span>
            <span className="text-titulo font-extrabold" data-e2e="descuento-producto-unitario">
              {formatearCOP(unitarioNeto)}
            </span>
          </div>
          {mostrarImpuesto && (
            <div className="px-4">
              <span className="text-muted block text-etiqueta font-bold uppercase">Impuesto</span>
              <span className="text-titulo font-extrabold" data-e2e="descuento-producto-impuesto">
                +{formatearCOP(Math.round(impuesto / linea.cantidad))}
              </span>
            </div>
          )}
          <div className="px-4">
            <span className="text-muted block text-etiqueta font-bold uppercase">Subtotal</span>
            <span className="text-titulo font-extrabold" data-e2e="descuento-producto-total">
              {formatearCOP(neto + impuesto)}
            </span>
          </div>
        </div>

        <FilaAcciones>
          {previo && (
            <Boton variante="contorno" onClick={() => onAplicar(sku, null)} data-e2e="descuento-quitar">
              Quitar descuento
            </Boton>
          )}
          <Boton variante="contorno" onClick={onCerrar}>
            Volver
          </Boton>
          <Boton disabled={!listo} onClick={() => candidato && onAplicar(sku, candidato)} data-e2e="descuento-aplicar">
            Aplicar
          </Boton>
        </FilaAcciones>
      </div>
    </Modal>
  );
}
